import { getEditorHtml } from '@/components/Editor/parse-html'
import { useAiWriter } from '@/hooks/useAiWriter'
import { AiWriterOption, GenerateData, GenerateType } from '@/types/aiAsk'
import { IArticleTextData, TextType } from '@/types/aiWriting'
// import { handleCommonText, handleText2Img, handleText2Visual } from '@/utils/aiArticle'
import { createStreamIterator } from '@/utils/outline'
import { marked } from 'marked'
import { useRef, useState } from 'react'
import { useModel } from 'umi'
import { v4 as uuidv4 } from 'uuid'
// @ts-ignore
import { ELKReportName } from '@/types'
import { elkReport } from '@/utils'
import escapeHtml from 'escape-html'

const useAIGenerating = () => {
  const { setAiWritingLoadding, promopt, dataBaseList } = useModel('aiWritingModel')
  const { methodsRef, editor } = useModel('editor')
  const { curPageIfm, updatePageInfo } = useModel('pageModel')
  const [generateData, setGenerateData] = useState<GenerateData>({
    leftIsEmpty: false, // 左侧是否为null
    type: GenerateType.NO,
  })

  const [articleTitle, setArticleTitle] = useState('')
  const [articleContent, setArticleContent] = useState('') // 用于左侧展示
  const articlePromptRef = useRef('') // 用AI Writing写文的prompt，只有插入到编辑器后才算有效
  const generateDataRef = useRef<GenerateData>(generateData)
  const stopAIRequestRef = useRef(false) // 是否终止AI流式请求，为了在手动close接口时，让缓存池的定时遍历停止
  const [AIReferenceList, setAIReferenceList] = useState<any[]>([]) // AI Writing文章的引用
  const [generatingArticle, setGeneratingArticle] = useState(false) // 是否正在生成文章
  const sideEditorRef: any = useRef()

  const { ask, close } = useAiWriter({ dependOnUser: true }) // 该ask与close转用于大纲的流式请求
  const commonIntervalTime = 50 // 通用定时时间

  // 处理最终的生成文章
  const askArticle = (data: AiWriterOption) => {
    let timer: any
    let title = '' // 标题
    let titleFinish = false // 标题是否结束
    let articleBegin = false // 正文是否开始
    setGeneratingArticle(true)

    // 重新生成文章到主体中，如果此时有上一篇文章的引用，先清空引用
    if (generateDataRef.current.type === GenerateType.GENE_IN_MAIN && curPageIfm.reference) {
      updatePageInfo({
        reference: '',
      })
    }

    setAIReferenceList([])

    return new Promise((resolve) => {
      const cacheList: IArticleTextData[] = []
      let htmlText = '' // 用于存储html文本
      let currentTagId = '' // 当前所要处理的标签的id,通用变量，使用时赋值，使用完毕后清空
      const step = 15 // 迭代的步长
      const iterrator = createStreamIterator(cacheList, step)
      const beginTime = Date.now()

      const askMsgCallback = (data: IArticleTextData) => {
        if (!data) {
          return
        }
        if (data.type === TextType.PROMPT) {
          articlePromptRef.current = data.text
          return
        }
        cacheList.push(data)
      }

      const askCloseCb = () => {
        setAiWritingLoadding(false)
        console.log('AI生文数据', cacheList)
        iterrator.markStreakClosed()
        const endTime = Date.now()
        // 生成所需时间,单位秒,
        const time = ((endTime - beginTime) / 1000).toFixed(2)
        elkReport({
          name: ELKReportName.aiArticleGenerateTime,
          message: `AI文章生成时间：${time}秒`,
          grade: 'Info',
          category: 'promise',
        })
      }

      // 处理AI Writing的预设数据，如果返回true，后续不再执行
      const handlePreset = (value: IArticleTextData[], done: boolean) => {
        if (done) {
          const referenceList = dataBaseList.map((ele) => {
            return {
              title: ele.title,
              content: ele.content,
              source: ele.source,
            }
          })
          setAIReferenceList(referenceList)
        }

        if (done && generateDataRef.current.type === GenerateType.GENE_IN_MAIN) {
          handleArticleReference()
        }

        if (done || stopAIRequestRef.current) {
          clearInterval(timer)
          if (generateDataRef.current.type === GenerateType.GENE_IN_MAIN) {
            // 如果是默认插入到编辑器中时，结束后使用AI Writing的prompt
            updatePageInfo({
              prompt: articlePromptRef.current,
            })
          }
          stopAIRequestRef.current = false
          setAiWritingLoadding(false)
          setGeneratingArticle(false)
          resolve('')
          return true
        }

        if (!value || value.length === 0) {
          return true
        }
      }

      /**
       * 预处理数据，整合同类型的数据到一起
       * 遍历数据，处理文本，整合同类型的数据到一起，降低编辑器的处理次数
       * 往后遍历过程中，如果type类型在newList中最后一个数据没有对应的数据就新增
       * 如果和newList中对后一个数据的type一样就把text拼接到最后一个数据的text后面
       * 注：这种情况下可能存在一次拿到某个类型的全部数据，也可能拿到某个类型的部分数据
       */
      const preHandleValues = (value: IArticleTextData[]) => {
        let list = []
        for (let i = 0; i < value.length; i++) {
          const currentData = value[i]
          if (currentData.type === TextType.TITLE && currentData.finish) {
            titleFinish = true
          }

          // 正文的第一个文本
          const firstArticleText = currentData.type === TextType.TEXT && titleFinish && !articleBegin
          if (firstArticleText) {
            articleBegin = true
            // 如果文本开头有若干换行符，去掉换行符
            currentData.text = currentData.text.replace(/^\n+/, '')
          }

          if (list.length === 0) {
            list.push(currentData)
            continue
          }
          const lastData: any = list[list.length - 1]
          if (lastData.type === currentData.type) {
            list[list.length - 1] = {
              ...lastData,
              ...currentData,
              text: lastData.text + currentData.text,
            }
          } else {
            list.push(currentData)
          }
        }
        return list
      }

      const getByInterval = () => {
        timer = setInterval(() => {
          const { value, done } = iterrator.next() as { value: IArticleTextData[]; done: boolean }
          const preset = handlePreset(value, done)
          if (preset) {
            return
          }

          let tempText = ''
          const newList = preHandleValues(value)

          for (let currentData of newList) {
            tempText += currentData.text
            if (currentData.type === TextType.TITLE) {
              title += currentData.text
              generateDataRef.current.type === GenerateType.GENE_IN_SIDE && setArticleTitle(title)
              generateDataRef.current.type === GenerateType.GENE_IN_MAIN &&
                updatePageInfo({
                  title,
                })

              if (currentData.finish) {
                titleFinish = true
              }
            }

            if (!titleFinish) {
              continue
            }

            if (currentData.type === TextType.TEXT) {
              htmlText += currentData.text
              continue
            }

            if (currentData.type === TextType.TEXT2IMG || currentData.type === TextType.VISUAL) {
              const typeMap: any = {
                [TextType.TEXT2IMG]: 'text2img',
                [TextType.VISUAL]: 'text2visual',
              }
              const tagName = typeMap[currentData.type]
              // 如果是start，创建自定义元素标签
              if (currentData.start) {
                currentTagId = uuidv4()
                htmlText += `<${tagName} id='${currentTagId}' data='${escapeHtml(JSON.stringify(currentData))}'></${tagName}>`
                continue
              }

              // 其他情况：解析htmlText，并根据id找到对应的text2img标签，更新data
              const parser = new DOMParser()
              const doc = parser.parseFromString(htmlText, 'text/html')
              const element = doc.getElementById(currentTagId)
              if (element) {
                // 获取data属性，并合并里面的text，最后再设置回去
                const dataStr = element.getAttribute('data') || '{}'
                // 先对dataStr进行反转义,下面处理完数据后会进行转义，防止多次转义导致后面的数据无法解析
                let elementData = JSON.parse(dataStr)
                const newData = {
                  ...elementData,
                  ...currentData,
                  text: elementData.text + currentData.text,
                }
                // 下面会通过innerHTML获取内容，会对特殊字符进行转移，所以这里不能进行转义，否则会导致双重转义
                element.setAttribute('data', JSON.stringify(newData))
                // 更新htmlText（innerHTML这里会对特殊字符进行转义）
                htmlText = doc.body.innerHTML
              }
            }
          }
          const resHtmlText = marked(htmlText)
          // 将resHtmlText插入到编辑器中
          methodsRef.current?.insertHTML(resHtmlText, true)

          // 遍历整合后的数据
          // for (let i = 0; i < newList.length; i++) {
          //   const currentData = newList[i]
          //   tempText += currentData.text
          //   // 当某个类型的数据处于开始阶段(如果是一次性拿到完整的prompt，finish也是true)，创建对应的id
          //   if (currentData.start) {
          //     currentTagId = uuidv4()
          //   }

          //   // 处理标题
          //   if (currentData.type === TextType.TITLE) {
          //     title += currentData.text
          //     generateDataRef.current.type === GenerateType.GENE_IN_SIDE && setArticleTitle(title)
          //     generateDataRef.current.type === GenerateType.GENE_IN_MAIN &&
          //       updatePageInfo({
          //         title,
          //       })
          //   }
          //   // 当前要处理的编辑器
          //   const currentEditor = generateDataRef.current.type === GenerateType.GENE_IN_MAIN ? editor : sideEditorRef.current
          //   // 如果是普通文本，把文本插入到文章末尾
          //   if (currentData.type === TextType.TEXT) {
          //     handleCommonText(currentData, currentEditor)
          //     continue
          //   }

          //   // 处理文生图
          //   if (currentData.type === TextType.TEXT2IMG) {
          //     handleText2Img(currentData, currentTagId, currentEditor)
          //     continue
          //   }
          //   // 处理可视化
          //   if (currentData.type === TextType.VISUAL) {
          //     handleText2Visual(currentData, currentTagId, currentEditor)
          //   }
          // }

          setArticleContent((preVal) => {
            return preVal + tempText
          })
        }, commonIntervalTime)
      }

      ask(
        { ...promopt, ...data },
        {
          openCb: getByInterval,
          msgCb: askMsgCallback,
          closeCb: askCloseCb,
        },
      )
    })
  }

  const insertNewContent = () => {
    if (articleTitle) {
      updatePageInfo({
        title: articleTitle,
      })
    }

    // 获取侧边编辑器的内容
    const sideEditor = sideEditorRef.current
    if (sideEditor) {
      // 获取侧边编辑器的html内容
      const html = getEditorHtml(sideEditor)
      // 覆盖编辑器的内容
      methodsRef.current?.insertHTML(html, true)
    }
  }

  const updateGenerateData = (data: GenerateData) => {
    if (data.type === GenerateType.OVERRIDE) {
      handleArticleReference()
      insertNewContent()
      // 如果是【替换左侧原文】，使用AI Writing的prompt
      updatePageInfo({
        prompt: articlePromptRef.current,
      })
    }

    if (data.type === GenerateType.GENE_IN_SIDE) {
      setArticleContent('')
      setArticleTitle('')
    }
    setGenerateData(data)
    generateDataRef.current = data
  }

  // 终止AI Writing的请求
  const onStop = () => {
    stopAIRequestRef.current = true
    close()
  }

  // 处理AI Writing文章的引用
  const handleArticleReference = () => {
    const referenceList = dataBaseList.map((ele) => {
      return {
        title: ele.title,
        content: ele.content,
        source: ele.source,
      }
    })
    setAIReferenceList(referenceList)
    updatePageInfo({
      reference: JSON.stringify(referenceList),
    })
  }

  return {
    askArticle,
    onStop,
    articleTitle,
    articleContent,
    generateData,
    articlePromptRef,
    updateGenerateData,
    generatingArticle,
    AIReferenceList,
    sideEditorRef,
  }
}

export default useAIGenerating
